import {SanityImage} from 'sanity-image'
import {useMemo} from 'react'
import clsx from 'clsx'
import {SanityImageCrop, SanityImageHotspot} from 'sanity.types'
import useShopConfig from '~/hooks/useShopConfig'

export type ImageProps = {
    hotspot?: SanityImageHotspot
    crop?: SanityImageCrop
    metadata?: {
        lqip?: string | null
    }
    alt: string
    asset?: {
        _ref: string
    }
}

type CmsImageProps = {
    className?: string
    image: ImageProps
    backgroundColor?: string
    expectedSize?: {
        width?: number
        height?: number
    }
    quality?: number
    mode?: 'cover' | 'contain'
    loading?: 'eager' | 'lazy'
    fetchPriority?: 'high' | 'low' | 'auto'
    sizes?: string
}

const CmsImage = ({
    className,
    image,
    backgroundColor,
    expectedSize,
    mode,
    sizes,
    quality,
    loading,
    fetchPriority,
}: CmsImageProps) => {
    const {cms} = useShopConfig()

    const {x, y} = image.hotspot ?? {}
    // noinspection com.intellij.reactbuddy.ExhaustiveDepsInspection
    const hotspot = useMemo(() => {
        if (!x || !y) {
            return undefined
        }
        return {x, y}
    }, [x, y])

    const {bottom, left, right, top} = image.crop ?? {}
    // noinspection com.intellij.reactbuddy.ExhaustiveDepsInspection
    const crop = useMemo(() => {
        if (!bottom || !left || !right || !top) {
            return undefined
        }
        return {bottom, left, right, top}
    }, [bottom, left, right, top])

    return (
        <SanityImage
            id={image.asset?._ref ?? ''}
            className={clsx(className)}
            style={{backgroundColor: backgroundColor}}
            projectId={cms.projectId}
            dataset={cms.dataset}
            width={expectedSize?.width}
            height={expectedSize?.height}
            loading={loading}
            fetchPriority={fetchPriority}
            mode={mode}
            hotspot={hotspot}
            crop={crop}
            preview={image.metadata?.lqip ?? undefined}
            alt={image.alt}
            sizes={sizes}
            queryParams={{
                q: quality ?? 75,
            }}
        />
    )
}

export default CmsImage
